import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@b2bportal/core-types";
import { AuthInfoSelectors } from "../auth";
import * as AccountSelectors from "./selectors";
import { AccountInfoActions } from "./slice";
import { fetchPointsBalance } from "./thunks/fetchPointsBalance";

export const useGetPointsBalance = () => {
  const pointsBalanceState = useSelector(AccountSelectors.getPointsBalance);
  const dispatch = useDispatch<AppDispatch>();

  const hasRewards = useSelector(AuthInfoSelectors.getHasRewards);
  const isVerified = useSelector(AuthInfoSelectors.getIsVerified);

  const pointsBalance = pointsBalanceState?.resource?.data;
  const pointsBalanceError = pointsBalanceState?.error;

  const isPointsEnabled = useMemo(
    () => Boolean(isVerified && hasRewards),
    [isVerified, hasRewards]
  );

  // Fetch points balance
  const getPointsBalance = useCallback(
    (forceUpdate = false) => {
      if (forceUpdate && isPointsEnabled) {
        dispatch(fetchPointsBalance());
      }
    },
    [dispatch, isPointsEnabled]
  );

  const clearPointsBalanceError = useCallback(() => {
    // Clear the error after showing it to the user once
    dispatch(AccountInfoActions.clearError());
  }, [dispatch]);

  const basePointsToCashMultiplier =
    pointsBalanceState?.resource?.data?.basePointsToCashMultiplier;

  const businessClassPointsToCashMultiplier =
    pointsBalanceState?.resource?.data?.businessClassPointsToCashMultiplier;

  return useMemo(
    () => ({
      getPointsBalance,
      clearPointsBalanceError,
      basePointsToCashMultiplier,
      businessClassPointsToCashMultiplier,
      isPointsEnabled,
      pointsBalance,
      pointsBalanceError,
    }),
    [
      getPointsBalance,
      clearPointsBalanceError,
      basePointsToCashMultiplier,
      businessClassPointsToCashMultiplier,
      isPointsEnabled,
      pointsBalance,
      pointsBalanceError,
    ]
  );
};
